import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ronPhoto from '../assets/first night 2015.png';

export default function ContactPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // State engine managing the Option B overlay visibility
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Dynamic state routing variables to track wizard screens independently
  const [modalAcceptedCookies, setModalAcceptedCookies] = useState(false);
  const [inlineAcceptedCookies, setInlineAcceptedCookies] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    phone: '',
    trade: ''
  });

  const [openFAQIndex, setOpenFAQIndex] = useState(null);

  // OPTION 1: Redirection Engine Handler 
  // Replace the URL string below with your actual live Calendly event link
  // OPTION 1: Redirection Engine Handler 
  const handleCalendarRedirect = () => {
    window.open('https://calendly.com/atlas_a1/30min', '_blank', 'noopener,noreferrer');
  };
  // Hook listening for trigger=popup transitions
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('trigger') === 'popup') {
      setIsModalOpen(true);
      
      // Clean up the URL query param back to clean contact state seamlessly
      navigate('/contact', { replace: true });
    }
  }, [location, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleFAQ = (index) => {
    setOpenFAQIndex(openFAQIndex === index ? null : index);
  };

  const faqData = [
    {
      question: "How fast can Atlas AI be deployed to our dispatch system?",
      answer: "Most plumbing and electrical platforms can be integrated within 3 to 5 business days. Our engineering team handles the entire connection pipeline so your team experiences zero downtime."
    },
    {
      question: "Does this replace our live human dispatchers?",
      answer: "Not at all. Atlas AI acts as an intelligent safety net. It intercepts the calls your team misses after-hours or during peak high-volume slots, qualifying the lead and booking them directly into your workflow management software."
    },
    {
      question: "Is our client data safe and secure?",
      answer: "Completely. All data handled by the platform travels through enterprise-grade, AES-256 encrypted channels and operates entirely within a strict SOC2 compliant infrastructure pipeline."
    }
  ];

  // Reusable Multi-Step Calendar Component Engine
  const CalendarWizard = ({ isModalContext, acceptedCookies, setAcceptedCookies }) => (
    <div className="w-full bg-white rounded-2xl overflow-hidden border border-gray-200 min-h-[650px] flex flex-col text-black relative">
      
      {/* Scaled Up Calendly Ribbon Asset */}
      <div className="absolute top-0 right-0 w-32 h-32 overflow-hidden pointer-events-none">
        <div className="absolute top-5 -right-10 rotate-45 bg-gray-500 text-white text-[9px] font-bold py-1 px-10 tracking-wide uppercase text-center shadow">
          Powered By<br />
          Calendly
        </div>
      </div>

      {/* Modern High-Resolution Profile Header Block */}
      <div className="flex flex-col items-center text-center pt-4 pb-2">
        <div className="w-16 h-16 rounded-full overflow-hidden shadow-sm mb-3">
          <img
            src={ronPhoto}
            alt="Ron Madison"
            className="w-full h-full object-cover"
          />
        </div>

        <h4 className="text-[15px] font-bold text-gray-500">
          Ron Madison
        </h4>

        <h2 className="text-[22px] font-black text-[#163a6b] tracking-tight mt-1">
          30 Minute Meeting
        </h2>

        <div className="flex items-center justify-center gap-2 mt-4 text-[#163a6b] font-bold">
          <span className="text-xl">🕒</span>
          <span>30 min</span>
        </div>
      </div>

      {/* Internal Screen Flow Switch Matrix */}
      <div className="p-5 flex flex-col flex-grow justify-between">
        
        {!acceptedCookies ? (
          /* STEP 1: COOKIE BANNER SCREEN INTERFACE */
          <div className="bg-gray-50 border border-gray-100 p-4 rounded-xl text-left space-y-3 relative z-10 shadow-sm my-auto">
            <p className="text-[10px] text-gray-500 leading-normal font-medium">
              We use cookies and similar technologies to improve and personalize your experience, to understand website interactions (including clicks, cursor movement and screen recordings), and for marketing. Click “Decline” to decline optional cookies or “Cookie Settings” to manage individual settings. To learn more, visit our <span className="text-blue-600 underline cursor-pointer">privacy notice</span>.
            </p>
            <div className="flex items-center justify-between pt-1 border-t border-gray-200/40">
              <button type="button" className="text-[10px] font-bold text-blue-600 hover:underline">Cookie settings →</button>
              <div className="flex items-center gap-3">
                <button 
                  type="button" 
                  onClick={() => { if (isModalContext) { setIsModalOpen(false); } }} 
                  className="text-[10px] font-bold text-gray-500 hover:text-gray-700"
                >
                  Decline
                </button>
                <button 
                  type="button" 
                  onClick={() => setAcceptedCookies(true)} 
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-bold rounded-lg shadow-sm transition-colors"
                >
                  I understand
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* STEP 2: EMBEDDED CALENDLY MONTH MATRIX SCREEN (WITH ACTION HANDLERS CONNECTED) */
          <div className="w-full flex flex-col flex-grow justify-center py-4 space-y-4 animate-in fade-in duration-200 text-center">
            <div className="w-full border-t border-gray-200 mb-6"></div>
            
            <h3 className="text-sm font-bold text-gray-800">Select a Day</h3>
            
            <div className="flex items-center justify-between px-8 text-xs font-bold text-blue-600">
              <button type="button" className="hover:text-blue-800">‹</button>
              <span className="text-gray-800 font-extrabold">June 2026</span>
              <button type="button" className="hover:text-blue-800">›</button>
            </div>

            <div className="grid grid-cols-7 gap-1 text-[9px] font-bold text-gray-400 uppercase tracking-wider px-2">
              <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
            </div>

            <div className="grid grid-cols-7 gap-y-2 gap-x-1 px-2 text-xs font-bold text-gray-800">
              <span className="text-gray-300 py-1"></span>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">1</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">2</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">3</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">4</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">5</button>
              <span className="text-gray-400 py-1 flex items-center justify-center">6</span>

              <span className="text-gray-400 py-1 flex items-center justify-center">7</span>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">8</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">9</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">10</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">11</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">12</button>
              <span className="text-gray-400 py-1 flex items-center justify-center">13</span>

              <span className="text-gray-400 py-1 flex items-center justify-center">14</span>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">15</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">16</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">17</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">18</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">19</button>
              <span className="text-gray-400 py-1 flex items-center justify-center">20</span>

              <span className="text-gray-400 py-1 flex items-center justify-center">21</span>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">22</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">23</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">24</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">25</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">26</button>
              <span className="text-gray-400 py-1 flex items-center justify-center">27</span>

              <span className="text-gray-400 py-1 flex items-center justify-center">28</span>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">29</button>
              <button type="button" onClick={handleCalendarRedirect} className="w-8 h-8 mx-auto rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center">30</button>
            </div>
          </div>
        )}

        <div className="w-full text-center text-[10px] text-gray-400 font-medium pt-2 border-t border-gray-100 mt-auto">
          Cookie preferences
        </div>
      </div>

    </div>
  );

  return (
    <div className="min-h-screen bg-[#030712] text-white flex flex-col relative">
      
      {/* OPTION B DYNAMIC OVERLAY MODAL PORTAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#070b14] border border-gray-900 rounded-3xl p-6 w-full max-w-md relative animate-in fade-in zoom-in-95 duration-150">
            <button 
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-white text-lg font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-900"
            >
              ✕
            </button>
            <div className="space-y-4 text-left mt-2">
              <div className="space-y-1">
                <h3 className="text-sm font-bold text-white tracking-tight flex items-center gap-1.5">
                  📅 Instant Strategy Session
                </h3>
                <p className="text-[11px] text-gray-400">
                  Lock in an engineering review with an Atlas AI systems expert below.
                </p>
              </div>
              <CalendarWizard 
                isModalContext={true} 
                acceptedCookies={modalAcceptedCookies} 
                setAcceptedCookies={setModalAcceptedCookies} 
              />
            </div>
          </div>
        </div>
      )}

      {/* GLOBAL NAVIGATION HEADER */}
      <nav className="border-b border-gray-900/80 bg-[#030712]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div onClick={() => navigate('/')} className="flex items-center gap-2.5 cursor-pointer select-none group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#c026d3] to-[#a855f7] flex items-center justify-center shadow-lg shadow-purple-500/20 group-hover:scale-105 transition-transform">
              <span className="text-white text-lg font-bold">⚡</span>
            </div>
            <span className="text-xl font-extrabold tracking-tight text-white">
              YourAtlas <span className="text-[#a855f7]">AI</span>
            </span>
          </div>

          <div className="hidden md:flex items-center gap-8 text-sm font-semibold text-gray-400">
            <button type="button" onClick={() => navigate('/')} className="hover:text-white transition-colors">Home</button>
            <button type="button" onClick={() => navigate('/features')} className="hover:text-white transition-colors">Features</button>
            <button type="button" onClick={() => navigate('/calculator')} className="hover:text-white transition-colors">Calculator</button>
            <button type="button" onClick={() => navigate('/media')} className="hover:text-white transition-colors">Media</button>
            <button type="button" onClick={() => navigate('/contact')} className="text-white border-b-2 border-[#a855f7] pb-1 cursor-default">Contact</button>
          </div>
        </div>
      </nav>

      {/* MAIN CONTAINER WORKSPACE */}
      <main className="max-w-7xl mx-auto w-full px-6 pt-12 pb-24 flex-grow">
        
        {/* Onboarding Intro Header Matrix */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <span className="px-3 py-1 rounded-full bg-[#a855f7]/10 text-[#a855f7] border border-[#a855f7]/20 text-xs font-semibold tracking-wide uppercase font-mono">
            Onboarding Center
          </span>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
            Let's Get Your Dispatch<br />Automated
          </h1>
          <p className="text-xs md:text-sm font-bold tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-rose-400 to-purple-400 uppercase">
            AI Trade Support for Electricians, Plumbers, HVAC
          </p>
          <p className="text-gray-400 text-xs md:text-sm max-w-xl mx-auto leading-relaxed pt-2">
            Book a live system integration walkthrough or drop us a line below to see how Atlas AI secures your premium trade leads 24/7.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start max-w-6xl mx-auto">
          
          {/* LEFT PANEL: Inline Calendar Block */}
          <div className="lg:col-span-5 bg-[#070b14] border border-gray-900 rounded-2xl p-6 space-y-4 text-left">
            <div className="space-y-1">
              <h3 className="text-sm font-bold text-white tracking-tight flex items-center gap-1.5">
                📅 Instant Strategy Session
              </h3>
              <p className="text-[11px] text-gray-400 leading-normal">
                Skip the back-and-forth emails. Select a time block below to lock in an engineering review with an Atlas AI systems expert.
              </p>
            </div>
            <CalendarWizard 
              isModalContext={false} 
              acceptedCookies={inlineAcceptedCookies} 
              setAcceptedCookies={setInlineAcceptedCookies} 
            />
          </div>

          {/* RIGHT PANEL: Custom Submission Form Intake */}
          <div className="lg:col-span-7 bg-[#070b14] border border-gray-900 rounded-2xl p-8 text-left space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Full Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="John Doe" className="w-full bg-[#03060f] border border-gray-900 focus:border-purple-500/40 rounded-xl px-4 py-3 text-xs text-white placeholder-gray-700 focus:outline-none transition-colors" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Company Name</label>
                <input type="text" name="companyName" value={formData.companyName} onChange={handleInputChange} placeholder="Apex Plumbing Systems" className="w-full bg-[#03060f] border border-gray-900 focus:border-purple-500/40 rounded-xl px-4 py-3 text-xs text-white placeholder-gray-700 focus:outline-none transition-colors" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Email Address</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="john@company.com" className="w-full bg-[#03060f] border border-gray-900 focus:border-purple-500/40 rounded-xl px-4 py-3 text-xs text-white placeholder-gray-700 focus:outline-none transition-colors" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Phone Number</label>
                <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="(555) 000-0000" className="w-full bg-[#03060f] border border-gray-900 focus:border-purple-500/40 rounded-xl px-4 py-3 text-xs text-white placeholder-gray-700 focus:outline-none transition-colors" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Core Trade Specialization</label>
              <select name="trade" value={formData.trade} onChange={handleInputChange} className="w-full bg-[#03060f] border border-gray-900 focus:border-purple-500/40 rounded-xl px-4 py-3 text-xs text-white focus:outline-none transition-colors appearance-none cursor-pointer">
                <option value="" disabled>Select your primary trade category...</option>
                <option value="plumbing">Plumbing Contractor</option>
                <option value="electrical">Electrical Contractor</option>
                <option value="hvac">HVAC Specialist</option>
                <option value="multi">Multi-Trade Enterprise</option>
              </select>
            </div>

            <button type="button" className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 font-bold rounded-xl text-xs tracking-wider uppercase transition-all shadow-lg shadow-purple-600/10">
              Deploy Automation Review Request
            </button>

            <div className="pt-4 border-t border-gray-900/60 flex flex-wrap items-center justify-between gap-4 text-[10px] font-medium text-gray-500">
              <div className="flex items-center gap-1">
                <span className="text-amber-500">★★★★★</span>
                <span>Trusted by 120+ Electrical & Plumbing Hubs</span>
              </div>
              <div className="flex items-center gap-4">
                <span>🔒 AES-256 Encrypted</span>
                <span>🛡️ SOC2 Compliant Pipeline</span>
              </div>
            </div>
          </div>

        </div>

        {/* FREQUENTLY ASKED QUESTIONS ACCORDION PANEL */}
        <div className="max-w-4xl mx-auto w-full mt-24 border-t border-gray-900/60 pt-16">
          <div className="text-center space-y-2 mb-12">
            <h2 className="text-2xl font-black tracking-tight text-white">Frequently Asked Questions</h2>
            <p className="text-gray-400 text-xs">Everything you need to know about setting up your automation pipeline.</p>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, index) => {
              const isOpen = openFAQIndex === index;
              return (
                <div 
                  key={index} 
                  className="bg-[#070b14] border border-gray-900 rounded-2xl overflow-hidden transition-all duration-200"
                >
                  <button
                    type="button"
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left font-bold text-sm text-white hover:text-purple-400 transition-colors"
                  >
                    <span>{faq.question}</span>
                    <span className={`text-xs text-gray-500 transform transition-transform duration-200 ${isOpen ? 'rotate-180 text-purple-400' : ''}`}>
                      ▼
                    </span>
                  </button>
                  
                  <div 
                    className={`transition-all duration-200 ease-in-out overflow-hidden ${isOpen ? 'max-h-40 border-t border-gray-900/40' : 'max-h-0'}`}
                  >
                    <p className="p-6 text-xs text-gray-400 leading-relaxed bg-[#040811]/40">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </main>

      {/* FOOTER SYSTEM ELEMENT */}
      <footer className="bg-[#01040a] border-t border-gray-950 text-left py-6 mt-auto">
        <div className="max-w-7xl mx-auto px-6 text-[11px] font-mono text-gray-600 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© 2026 Atlas AI. Powered by real AI for trades businesses.</p>
          <div className="flex gap-4 text-xs font-sans">
            <a href="https://twitter.com" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">Twitter</a>
            <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">LinkedIn</a>
          </div>
        </div>
      </footer>
    </div>
  );
}